import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Trash, Check, AlertCircle, FileText, CheckSquare, TrendingUp, FileCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MilestoneDocumentModal = ({ isOpen, onClose, projectId, milestone, documentType, onDocumentCreated }) => {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  
  // Document-specific content
  const [checklistItems, setChecklistItems] = useState([{ id: Date.now(), description: '', completed: false }]);
  const [qcPoints, setQcPoints] = useState([{ id: Date.now(), description: '', specification: '', result: 'pending' }]);
  const [decisions, setDecisions] = useState([{ id: Date.now(), decision: '', rationale: '', impact: 'scope' }]);
  const [supportContent, setSupportContent] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setTitle(getDefaultTitle());
      setNotes('');
      setStatus('draft');
      setChecklistItems([{ id: Date.now(), description: '', completed: false }]);
      setQcPoints([{ id: Date.now(), description: '', specification: '', result: 'pending' }]);
      setDecisions([{ id: Date.now(), decision: '', rationale: '', impact: 'scope' }]);
      setSupportContent('');
    }
  }, [isOpen, documentType, milestone]);

  const getDefaultTitle = () => {
    const type = documentType?.replace('_', ' ').toUpperCase() || 'DOCUMENT';
    return `${type} - ${milestone?.description || 'Milestone'}`;
  };

  const getDocumentIcon = () => {
    switch (documentType) {
      case 'qc_sheet': return <FileCheck size={20} className="text-green-500" />;
      case 'checklist': return <CheckSquare size={20} className="text-blue-500" />;
      case 'decision_planning': return <TrendingUp size={20} className="text-purple-500" />;
      case 'support': return <FileText size={20} className="text-orange-500" />;
      default: return <FileText size={20} />;
    }
  };

  // Checklist functions
  const addChecklistItem = () => {
    setChecklistItems([...checklistItems, { id: Date.now(), description: '', completed: false }]);
  };

  const removeChecklistItem = (id) => {
    setChecklistItems(checklistItems.filter(item => item.id !== id));
  };

  const updateChecklistItem = (id, field, value) => {
    setChecklistItems(checklistItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // QC functions
  const addQCPoint = () => {
    setQcPoints([...qcPoints, { id: Date.now(), description: '', specification: '', result: 'pending' }]);
  };

  const removeQCPoint = (id) => {
    setQcPoints(qcPoints.filter(point => point.id !== id));
  };

  const updateQCPoint = (id, field, value) => {
    setQcPoints(qcPoints.map(point => 
      point.id === id ? { ...point, [field]: value } : point
    ));
  };

  // Decision functions
  const addDecision = () => {
    setDecisions([...decisions, { id: Date.now(), decision: '', rationale: '', impact: 'scope' }]);
  };

  const removeDecision = (id) => {
    setDecisions(decisions.filter(dec => dec.id !== id));
  };

  const updateDecision = (id, field, value) => {
    setDecisions(decisions.map(dec => 
      dec.id === id ? { ...dec, [field]: value } : dec
    ));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    // Validate document-specific content
    if (documentType === 'checklist' && checklistItems.filter(item => item.description).length === 0) {
      toast.error('Please add at least one checklist item');
      return;
    }
    if (documentType === 'qc_sheet' && qcPoints.filter(point => point.description).length === 0) {
      toast.error('Please add at least one QC inspection point');
      return;
    }
    if (documentType === 'decision_planning' && decisions.filter(dec => dec.decision).length === 0) {
      toast.error('Please add at least one decision');
      return;
    }

    setSaving(true);
    try {
      let content = {};
      
      // Build content based on document type
      switch (documentType) {
        case 'checklist':
          content = { items: checklistItems.filter(item => item.description) };
          break;
        case 'qc_sheet':
          content = { inspection_points: qcPoints.filter(point => point.description) };
          break;
        case 'decision_planning':
          content = { decisions: decisions.filter(dec => dec.decision) };
          break;
        case 'support':
          content = { content: supportContent };
          break;
        default:
          break;
      }

      const response = await axios.post(
        `${API}/projects/${projectId}/milestones/${milestone.id}/documents`,
        {
          document_type: documentType,
          title,
          content,
          status,
          notes
        }
      );

      toast.success('Document created successfully');
      onDocumentCreated(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating document:', error);
      toast.error('Failed to create document');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-background border border-border rounded-lg max-w-4xl w-full my-8">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {getDocumentIcon()}
              <h2 className="text-2xl font-bold uppercase">
                Create {documentType?.replace('_', ' ')}
              </h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X size={20} />
            </Button>
          </div>

          {/* Milestone Info */}
          <div className="mb-6 p-3 bg-accent/20 border border-border rounded">
            <p className="text-sm font-mono uppercase text-muted-foreground">Milestone</p>
            <p className="font-medium">{milestone?.description}</p>
          </div>

          {/* Title */}
          <div className="mb-4">
            <Label htmlFor="title">Document Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter document title"
              className="mt-1"
            />
          </div>

          {/* Status */}
          <div className="mb-4">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-input rounded-sm bg-background"
            >
              <option value="draft">Draft</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="approved">Approved</option>
            </select>
          </div>

          {/* Document-specific content */}
          <div className="mb-6">
            {documentType === 'checklist' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label>Checklist Items</Label>
                  <Button size="sm" onClick={addChecklistItem}>
                    <Plus size={14} className="mr-1" />
                    Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {checklistItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Input
                        value={item.description}
                        onChange={(e) => updateChecklistItem(item.id, 'description', e.target.value)}
                        placeholder="Task description"
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeChecklistItem(item.id)}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {documentType === 'qc_sheet' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label>Inspection Points</Label>
                  <Button size="sm" onClick={addQCPoint}>
                    <Plus size={14} className="mr-1" />
                    Add Point
                  </Button>
                </div>
                <div className="space-y-3">
                  {qcPoints.map((point) => (
                    <Card key={point.id} className="p-3">
                      <div className="space-y-2">
                        <Input
                          value={point.description}
                          onChange={(e) => updateQCPoint(point.id, 'description', e.target.value)}
                          placeholder="Inspection point description"
                        />
                        <Input
                          value={point.specification}
                          onChange={(e) => updateQCPoint(point.id, 'specification', e.target.value)}
                          placeholder="Specification/Requirement"
                        />
                        <div className="flex items-center gap-2">
                          <select
                            value={point.result}
                            onChange={(e) => updateQCPoint(point.id, 'result', e.target.value)}
                            className="flex-1 px-3 py-2 border border-input rounded-sm bg-background text-sm"
                          >
                            <option value="pending">Pending</option>
                            <option value="pass">Pass</option>
                            <option value="fail">Fail</option>
                            <option value="na">N/A</option>
                          </select>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeQCPoint(point.id)}
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {documentType === 'decision_planning' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label>Decisions & Changes</Label>
                  <Button size="sm" onClick={addDecision}>
                    <Plus size={14} className="mr-1" />
                    Add Decision
                  </Button>
                </div>
                <div className="space-y-3">
                  {decisions.map((dec) => (
                    <Card key={dec.id} className="p-3">
                      <div className="space-y-2">
                        <Input
                          value={dec.decision}
                          onChange={(e) => updateDecision(dec.id, 'decision', e.target.value)}
                          placeholder="Decision or scope change"
                        />
                        <Textarea
                          value={dec.rationale}
                          onChange={(e) => updateDecision(dec.id, 'rationale', e.target.value)}
                          placeholder="Rationale and justification"
                          rows={2}
                        />
                        <div className="flex items-center gap-2">
                          <select
                            value={dec.impact}
                            onChange={(e) => updateDecision(dec.id, 'impact', e.target.value)}
                            className="flex-1 px-3 py-2 border border-input rounded-sm bg-background text-sm"
                          >
                            <option value="scope">Scope Change</option>
                            <option value="cost">Cost Impact</option>
                            <option value="timeline">Timeline Change</option>
                            <option value="quality">Quality Requirement</option>
                          </select>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeDecision(dec.id)}
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {documentType === 'support' && (
              <div>
                <Label>Support Documentation</Label>
                <Textarea
                  value={supportContent}
                  onChange={(e) => setSupportContent(e.target.value)}
                  placeholder="Enter support notes, additional information, or documentation..."
                  rows={8}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="mb-6">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes or comments"
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Creating...' : 'Create Document'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilestoneDocumentModal;
